import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  formatInvoiceNumber,
  invoiceIssuerSchema,
  invoiceNumberingSchema,
  type InvoiceSettingsDto,
} from "@tc/shared";
import { Button, Card, Field, Stepper, Textarea, TextInput, Toast } from "@/components/ui";
import { fetchInvoiceSettings, saveInvoiceIssuer, saveInvoiceNumbering } from "./invoicing-api";

/**
 * Settings → Invoicing (TW-3527) — the back-office org-wide factura config: the issuer block TC bills
 * under (company / tax number / contact / payment terms + instructions) and the running invoice
 * numbering scheme. Distinct from a club's own billing details (the recipient). Mounted only for
 * back-office users holding `invoice.settings` (see SettingsPage). The two sections save independently
 * (the design's two forms); both validate against the shared Zod schemas so the form and API agree.
 */
export function InvoicingSection() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<InvoiceSettingsDto | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchInvoiceSettings()
      .then((s) => alive && setSettings(s))
      .catch(() => alive && setLoadError(true));
    return () => {
      alive = false;
    };
  }, []);

  if (loadError) return <p className="muted">{t("settings.invoicing.loadError")}</p>;
  if (!settings) return <p className="muted">{t("common.loading")}</p>;

  return (
    <div>
      <div className="set-section-head" style={{ marginBottom: 18 }}>
        <h2>{t("settings.invoicing.heading")}</h2>
        <div className="sub">{t("settings.invoicing.sub")}</div>
      </div>
      <IssuerForm settings={settings} onSaved={setSettings} />
      <NumberingForm settings={settings} onSaved={setSettings} />
    </div>
  );
}

// ── Issuer details + payment ────────────────────────────────────────────────────

interface IssuerState {
  companyName: string;
  taxNo: string;
  email: string;
  phone: string;
  address: string;
  paymentTerms: string;
  paymentInstructions: string;
}

function toIssuer(s: InvoiceSettingsDto): IssuerState {
  return {
    companyName: s.companyName,
    taxNo: s.taxNo,
    email: s.email,
    phone: s.phone,
    address: s.address,
    paymentTerms: s.paymentTerms,
    paymentInstructions: s.paymentInstructions,
  };
}

function IssuerForm({ settings, onSaved }: { settings: InvoiceSettingsDto; onSaved: (s: InvoiceSettingsDto) => void }) {
  const { t } = useTranslation();
  const [initial, setInitial] = useState<IssuerState>(() => toIssuer(settings));
  const [form, setForm] = useState<IssuerState>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof IssuerState, string>>>({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const isDirty = useMemo(
    () => (Object.keys(form) as (keyof IssuerState)[]).some((k) => form[k].trim() !== initial[k].trim()),
    [form, initial],
  );

  function set<K extends keyof IssuerState>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  }

  function flash(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2600);
  }

  async function onSave() {
    const parsed = invoiceIssuerSchema.safeParse(form);
    if (!parsed.success) {
      const next: Partial<Record<keyof IssuerState, string>> = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as keyof IssuerState;
        if (field && !next[field]) next[field] = issue.message;
      }
      setErrors(next);
      return;
    }
    setSaving(true);
    try {
      const saved = await saveInvoiceIssuer(parsed.data);
      const synced = toIssuer(saved);
      setInitial(synced);
      setForm(synced);
      setErrors({});
      onSaved(saved);
      flash(t("settings.invoicing.saved"));
    } catch {
      flash(t("settings.invoicing.saveError"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Card>
        <div className="set-section-head">
          <h2>{t("settings.invoicing.issuer.title")}</h2>
          <div className="sub">{t("settings.invoicing.issuer.sub")}</div>
        </div>
        <div className="form-grid" style={{ marginTop: 18 }}>
          <Field label={t("settings.invoicing.companyName")} required error={errors.companyName} full>
            <TextInput icon="building" value={form.companyName} onChange={(v) => set("companyName", v)} />
          </Field>
          <Field label={t("settings.invoicing.taxNo")} required error={errors.taxNo} hint={t("settings.invoicing.taxNoHint")}>
            <TextInput value={form.taxNo} onChange={(v) => set("taxNo", v)} placeholder="ESB00000000" />
          </Field>
          <Field label={t("settings.invoicing.phone")} error={errors.phone}>
            <TextInput value={form.phone} onChange={(v) => set("phone", v)} placeholder="93 000 00 00" />
          </Field>
          <Field label={t("settings.invoicing.email")} required error={errors.email} full>
            <TextInput icon="mail" type="email" value={form.email} onChange={(v) => set("email", v)} />
          </Field>
          <Field label={t("settings.invoicing.address")} error={errors.address} full>
            <Textarea value={form.address} onChange={(v) => set("address", v)} rows={2} />
          </Field>
        </div>
      </Card>

      <Card style={{ marginTop: 18 }}>
        <div className="set-section-head">
          <h2>{t("settings.invoicing.payment.title")}</h2>
          <div className="sub">{t("settings.invoicing.payment.sub")}</div>
        </div>
        <div className="form-grid" style={{ marginTop: 18 }}>
          <Field label={t("settings.invoicing.paymentTerms")} error={errors.paymentTerms} full>
            <TextInput
              value={form.paymentTerms}
              onChange={(v) => set("paymentTerms", v)}
              placeholder={t("settings.invoicing.paymentTermsPh")}
            />
          </Field>
          <Field
            label={t("settings.invoicing.paymentInstructions")}
            error={errors.paymentInstructions}
            hint={t("settings.invoicing.paymentInstructionsHint")}
            full
          >
            <Textarea value={form.paymentInstructions} onChange={(v) => set("paymentInstructions", v)} rows={3} />
          </Field>
        </div>
        <div className="card-foot">
          {isDirty && (
            <span className="foot-note">
              <span className="badge-dot" style={{ background: "var(--warning)" }} />
              {t("settings.invoicing.unsaved")}
            </span>
          )}
          <Button variant="ghost" onClick={() => setForm(initial)} disabled={!isDirty || saving}>
            {t("actions.cancel")}
          </Button>
          <Button variant="primary" icon="check" onClick={onSave} disabled={!isDirty || saving}>
            {t("settings.invoicing.save")}
          </Button>
        </div>
      </Card>

      {toast && <Toast message={toast} />}
    </>
  );
}

// ── Numbering ─────────────────────────────────────────────────────────────────────

interface NumberingState {
  prefix: string;
  suffix: string;
  nextNumber: number;
  minDigits: number;
}

function NumberingForm({
  settings,
  onSaved,
}: {
  settings: InvoiceSettingsDto;
  onSaved: (s: InvoiceSettingsDto) => void;
}) {
  const { t } = useTranslation();
  const [initial, setInitial] = useState<NumberingState>(() => ({ ...settings.numbering }));
  const [form, setForm] = useState<NumberingState>(initial);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const isDirty = useMemo(
    () =>
      form.prefix !== initial.prefix ||
      form.suffix !== initial.suffix ||
      form.nextNumber !== initial.nextNumber ||
      form.minDigits !== initial.minDigits,
    [form, initial],
  );
  const canSave = isDirty && form.nextNumber >= 1;
  const preview = formatInvoiceNumber(form, form.nextNumber);
  const afterPreview = formatInvoiceNumber(form, form.nextNumber + 1);

  function flash(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2600);
  }

  async function onSave() {
    const parsed = invoiceNumberingSchema.safeParse(form);
    if (!parsed.success) return;
    setSaving(true);
    try {
      const saved = await saveInvoiceNumbering(parsed.data);
      const synced = { ...saved.numbering };
      setInitial(synced);
      setForm(synced);
      onSaved(saved);
      flash(t("settings.invoicing.saved"));
    } catch {
      flash(t("settings.invoicing.saveError"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card style={{ marginTop: 18 }}>
      <div className="set-section-head">
        <h2>{t("settings.invoicing.numbering.title")}</h2>
        <div className="sub">{t("settings.invoicing.numbering.sub")}</div>
      </div>

      <div className="seq-preview">
        <div className="seq-preview-label">{t("settings.invoicing.numbering.preview")}</div>
        <div className="seq-preview-val">{preview || "—"}</div>
        <div className="seq-preview-next">
          {t("settings.invoicing.numbering.after")} {afterPreview}
        </div>
      </div>

      <div className="form-grid" style={{ marginTop: 20 }}>
        <Field label={t("settings.invoicing.numbering.prefix")}>
          <TextInput value={form.prefix} onChange={(v) => setForm((f) => ({ ...f, prefix: v }))} placeholder="P-" />
        </Field>
        <Field label={t("settings.invoicing.numbering.suffix")}>
          <TextInput value={form.suffix} onChange={(v) => setForm((f) => ({ ...f, suffix: v }))} placeholder="/26" />
        </Field>
        <Field label={t("settings.invoicing.numbering.next")} required hint={t("settings.invoicing.numbering.nextHint")}>
          <TextInput
            type="number"
            min={1}
            value={String(form.nextNumber)}
            onChange={(v) => setForm((f) => ({ ...f, nextNumber: Math.max(0, parseInt(v.replace(/[^0-9]/g, "") || "0", 10)) }))}
          />
        </Field>
        <Field label={t("settings.invoicing.numbering.minDigits")} hint={t("settings.invoicing.numbering.minDigitsHint")}>
          <Stepper value={form.minDigits} min={0} max={10} onChange={(v) => setForm((f) => ({ ...f, minDigits: v }))} />
        </Field>
      </div>

      <div className="card-foot">
        {isDirty && (
          <span className="foot-note">
            <span className="badge-dot" style={{ background: "var(--warning)" }} />
            {t("settings.invoicing.unsaved")}
          </span>
        )}
        <Button variant="ghost" onClick={() => setForm(initial)} disabled={!isDirty || saving}>
          {t("actions.cancel")}
        </Button>
        <Button variant="primary" icon="check" onClick={onSave} disabled={!canSave || saving}>
          {t("settings.invoicing.save")}
        </Button>
      </div>

      {toast && <Toast message={toast} />}
    </Card>
  );
}
